import { FC, useEffect, useState } from 'react';
import VirtualBatteryCardList from '@/components/VirtualBattery/VirtualBatteryCardList';

import { LoadingAnimation } from '../LoadingAnimation';

import {
  Typography, Box,
  Grid, 
  FormControl, 
  Select, 
  MenuItem,
  Stack,
  useTheme,
} from '@mui/material';
import { Button } from '@gisce/oficina-virtual-components';
// import ContractElectronicNotification from "./ContractElectronicNotification";
// import BateriaVirtualDialogOpenButton from '../../BateriaVirtual/BateriaVirtualDialogOpenButton';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams, useParams } from "react-router";
import AttachmentsCard from '@/components/Contracts/components/DetailsPanels/AttachmentsDetailPanel';
import { ContractModificationTypes } from './types/contracts';
import ModificationDialog from './components/ModificationDialog';
import { useContracts } from './hooks/useContracts';
import { ContactDetailsPanel } from './components/DetailsPanels/ContactDetailsPanel';
import { ConsumptionDetailsPanel } from './components/DetailsPanels/ConsumptionDetailsPanel';
import { ElecContractDetailsPanel } from './components/DetailsPanels/ElecContractDetailsPanel';
import { SupplyDetails } from './components/DetailsPanels/SupplyDetailsPanel';
import { NavigateBefore } from '@mui/icons-material';
import CouponsDetailsPanel from './components/DetailsPanels/CouponsDetailsPanel';
import useIdNavigate from '@/hooks/useIdNavigate';
import { useSettingsContext } from '../Settings/hooks/useSettingsContext';

interface IContractDetailProps {
}

const ContractDetailElectricity: FC<IContractDetailProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const Settings = useSettingsContext();
  const { scrollToSection } = useIdNavigate();
  const {contractName} = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const { 
    status,
    contracts, 
    currentContract: contract,
    downloadContract,
    goToContract, 
    getContractFromName
  } = useContracts("electricity");

  const [ formContent, setFormContent ] = useState<ContractModificationTypes | null>(null);

  useEffect(() => {
    if (searchParams.get("action") && contract) {
      const currAction = searchParams.get("action");
      // Can't do this due to a stupid React Router DOM behaviour:
      // https://github.com/remix-run/react-router/issues/9304
      // searchParams.delete("action");
      setSearchParams({});
      switch(currAction) {
        case 'powChange':
          setFormContent('power');
          break;
        case 'ibanChange':
          setFormContent('iban');
          break;
        case 'download':
          downloadContract();
          break;
        case 'consumption':
          scrollToSection('consumption');
          break;
        case 'notifAddrChange':
          setFormContent('address');
          break;
        case 'noCesionSips':
          setFormContent('noSIPSCession');
          break;
        case 'reclam':
          setFormContent('reclam');
          break;
        case 'applyCoupon':
          scrollToSection('coupon-list');
          break;
      }
    }

  }, [searchParams.get("action"), contract, scrollToSection]);

  useEffect(() => {
    if (status === "succeeded" && !contract && contracts.length) {
      if (contractName && getContractFromName(contractName)) {
        goToContract(contractName);
      } else {
        navigate(t(`common:url.contracts.electricity.default`));
      }
    }
  }, [contracts, contract, status, contractName])
  
  if (status === "loading") {
    
    return <LoadingAnimation/>;

  } else if ( status === "succeeded"  && contract) {
    return contract && <>
      {formContent && <ModificationDialog 
        contract={contract} 
        formContent={formContent} 
        handleDialogClose={() => {setFormContent(null)}}
        type='electricity'
      />}
      
      <Stack spacing={2}>

        <Grid 
          container
          direction="row"
          alignItems="top"
        >
          <Grid item>
            <Typography variant='h4'>
              {t('common:text.contract_title')}
            </Typography>
          </Grid>
          <Grid>
            <FormControl>
              <Select
                  sx={{
                    "& .MuiInputBase-input": {
                      paddingTop: "10px",
                      paddingLeft: 1,
                      fontSize: theme.typography.h4.fontSize,
                      minHeight: "0.9em !important"
                    }
                  }}
                value={contract.name} 
                onChange={(event) => goToContract(event.target.value)}
              >
                {contracts.map(contract => <MenuItem
                    key={contract.id} value={contract.name}
                  >
                    {contract.name}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
              
        <Grid container>
          <Grid item>
            <Button 
              startIcon={<NavigateBefore />}
              onClick={() => {navigate(t("common:url.contracts.electricity.default"))}}
              sx={{
                color: "gray",
                ":hover": {
                  color: "black"
                },
                ":focus": {
                  color: "black"
                }
              }}
            >
              {t("common:text.contracts_see_list")}
            </Button>
          </Grid>
        </Grid>

        <ElecContractDetailsPanel 
          openDialog={setFormContent}
        />

        <ContactDetailsPanel
          type={"electricity"}
          openDialog={setFormContent}
        />

        <SupplyDetails type={"electricity"}/>
        
        {contract.bateria_virtual && Object.keys(contract.bateria_virtual).length > 0 &&
          <VirtualBatteryCardList />
        }

        <ConsumptionDetailsPanel type={"electricity"} />

        <AttachmentsCard attachmentsList={contract.attachments}/>

        {Settings.features.promotionalCode?.contractDetails?.showList && 
          <CouponsDetailsPanel 
            applyCoupon={Settings.features.promotionalCode.contractDetails.applyCoupon}
          />
        }

      </Stack>
    </>
  } else {
    return <></>
  }

}

export default ContractDetailElectricity;
